import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  Table,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import {
  getSuperAdminPlanVersions,
  SaPlan,
} from "../../../helpers/super_admin_plans_helper";
import { formatCents, intlLocaleFromI18n } from "../../../helpers/money";

type Props = {
  isOpen: boolean;
  plan: SaPlan | null;
  onClose: () => void;
  /** Abre o form de edição de uma versão da timeline. */
  onEdit: (plan: SaPlan) => void;
};

type DiffTone = "up" | "down" | "same" | null;

const diffToneClass = (tone: DiffTone): string => {
  if (tone === "up") {
    return "text-success";
  }
  if (tone === "down") {
    return "text-danger";
  }
  return "text-muted";
};

/**
 * Timeline de versões comerciais (LAC-063).
 * Mostra rows da mesma version_family + currency — grandfathering = novo row.
 * Diff vs versão imediatamente mais antiga (lista já vem mais recente primeiro).
 */
const PlanVersionsModal = ({ isOpen, plan, onClose, onEdit }: Props) => {
  const { t, i18n } = useTranslation();
  const [versions, setVersions] = useState<SaPlan[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!plan) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await getSuperAdminPlanVersions(plan.id);
      if (!res?.success) {
        setError(t("sa.plans.versions.error"));
        setVersions([]);
        return;
      }
      setVersions(res.data || []);
    } catch {
      setError(t("sa.plans.versions.error"));
      setVersions([]);
    } finally {
      setLoading(false);
    }
  }, [plan, t]);

  useEffect(() => {
    if (!isOpen || !plan) {
      setVersions([]);
      setError(null);
      setLoading(false);
      return;
    }
    load();
  }, [isOpen, plan, load]);

  const locale = intlLocaleFromI18n(i18n.language);

  const formatDate = (iso?: string) => {
    if (!iso) {
      return "—";
    }
    try {
      return new Intl.DateTimeFormat(locale, {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(iso));
    } catch {
      return iso;
    }
  };

  const formatSignedCents = (delta: number, currency: string) => {
    const abs = formatCents(Math.abs(delta), currency, locale);
    if (delta > 0) {
      return `+${abs}`;
    }
    if (delta < 0) {
      return `−${abs}`;
    }
    return abs;
  };

  const formatSignedQuota = (delta: number) => {
    const abs = new Intl.NumberFormat(locale).format(Math.abs(delta));
    if (delta > 0) {
      return `+${abs}`;
    }
    if (delta < 0) {
      return `−${abs}`;
    }
    return abs;
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="lg">
      <ModalHeader toggle={onClose}>
        {t("sa.plans.versions.title", { name: plan?.name || "" })}
      </ModalHeader>
      <ModalBody>
        <p className="text-muted small mb-3">
          {t("sa.plans.versions.intro", {
            family: plan?.version_family || plan?.slug || "—",
          })}
        </p>

        {loading ? (
          <div className="text-center py-4">
            <Spinner color="primary" />
            <div className="text-muted small mt-2">
              {t("sa.plans.versions.loading")}
            </div>
          </div>
        ) : null}

        {error ? (
          <Alert color="danger" className="mb-0">
            {error}
            <div className="mt-2">
              <Button color="danger" outline size="sm" onClick={load}>
                {t("sa.plans.retry")}
              </Button>
            </div>
          </Alert>
        ) : null}

        {!loading && !error && versions.length === 0 ? (
          <Alert color="info" className="mb-0">
            {t("sa.plans.versions.empty")}
          </Alert>
        ) : null}

        {!loading && !error && versions.length > 0 ? (
          <div className="table-responsive">
            <Table className="table-nowrap align-middle mb-0" size="sm">
              <thead className="table-light">
                <tr>
                  <th>{t("sa.plans.versions.col.when")}</th>
                  <th>{t("sa.plans.table.name")}</th>
                  <th>{t("sa.plans.table.price")}</th>
                  <th>{t("sa.plans.table.quota")}</th>
                  <th>{t("sa.plans.table.status")}</th>
                  <th className="text-end">{t("sa.plans.versions.col.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {versions.map((row, index) => {
                  const isCurrent = plan?.id === row.id;
                  const isLatest = index === 0;
                  // Lista desc: índice+1 = versão imediatamente anterior (mais antiga).
                  const older = versions[index + 1] ?? null;
                  const priceDelta = older
                    ? row.base_price_cents - older.base_price_cents
                    : null;
                  const quotaDelta = older
                    ? row.email_quota - older.email_quota
                    : null;
                  const priceTone: DiffTone =
                    priceDelta === null
                      ? null
                      : priceDelta > 0
                        ? "up"
                        : priceDelta < 0
                          ? "down"
                          : "same";
                  const quotaTone: DiffTone =
                    quotaDelta === null
                      ? null
                      : quotaDelta > 0
                        ? "up"
                        : quotaDelta < 0
                          ? "down"
                          : "same";
                  const subs = row.subscriptions_count ?? 0;

                  return (
                    <tr
                      key={row.id}
                      className={isCurrent ? "table-primary" : undefined}
                    >
                      <td className="text-nowrap small">
                        {formatDate(row.created_at)}
                        {isLatest ? (
                          <div>
                            <Badge color="soft-success" className="text-success">
                              {t("sa.plans.versions.badge.latest")}
                            </Badge>
                          </div>
                        ) : null}
                      </td>
                      <td>
                        <div className="fw-medium">{row.name}</div>
                        <div className="text-muted small">{row.slug}</div>
                        {subs > 0 ? (
                          <div className="mt-1">
                            <Link
                              to={`/super-admin/tenants?plan_id=${row.id}`}
                              className="link-primary small"
                              title={t(
                                "sa.plans.bulkDeactivate.viewTenantsTitle"
                              )}
                              onClick={onClose}
                            >
                              {t("sa.plans.versions.viewTenants", {
                                count: subs,
                              })}
                            </Link>
                          </div>
                        ) : null}
                      </td>
                      <td className="text-nowrap">
                        <div>
                          {formatCents(
                            row.base_price_cents,
                            row.currency || "BRL",
                            locale
                          )}
                        </div>
                        {priceTone !== null && priceDelta !== null ? (
                          <div
                            className={`small ${diffToneClass(priceTone)}`}
                            title={t("sa.plans.versions.diff.vsPrevious")}
                          >
                            {priceTone === "same"
                              ? t("sa.plans.versions.diff.same")
                              : formatSignedCents(
                                  priceDelta,
                                  row.currency || "BRL"
                                )}
                          </div>
                        ) : null}
                      </td>
                      <td className="text-nowrap">
                        <div>
                          {t("sa.plans.quotaValue", {
                            formatted: new Intl.NumberFormat(locale).format(
                              row.email_quota
                            ),
                          })}
                        </div>
                        {quotaTone !== null && quotaDelta !== null ? (
                          <div
                            className={`small ${diffToneClass(quotaTone)}`}
                            title={t("sa.plans.versions.diff.vsPrevious")}
                          >
                            {quotaTone === "same"
                              ? t("sa.plans.versions.diff.same")
                              : t("sa.plans.versions.diff.quota", {
                                  delta: formatSignedQuota(quotaDelta),
                                })}
                          </div>
                        ) : null}
                      </td>
                      <td>
                        {row.is_active ? (
                          <Badge color="success">
                            {t("sa.plans.status.active")}
                          </Badge>
                        ) : (
                          <Badge color="secondary">
                            {t("sa.plans.status.inactive")}
                          </Badge>
                        )}
                      </td>
                      <td className="text-end">
                        <Button
                          color="light"
                          size="sm"
                          onClick={() => onEdit(row)}
                        >
                          {isCurrent
                            ? t("sa.plans.versions.openCurrent")
                            : t("sa.plans.edit")}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        ) : null}
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={onClose}>
          {t("sa.plans.versions.close")}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default PlanVersionsModal;
